import { supabase } from '@/components/atomic-crm/providers/supabase/supabase';

import {
  nomeNoBucket,
  normalizarComentario,
  normalizarCurtida,
  normalizarPost,
  type Anexo,
  type Comentario,
  type Curtida,
  type Post,
} from './feedLogica';

/**
 * Acesso ao Feed North (migration 217). Tudo passa pela RLS de quem está
 * logado: o banco é quem decide quem vê o post, quem publica (só super admin)
 * e grava o nome do autor — nada disso vem do navegador.
 */

const BUCKET = 'feed';

// Tabelas da 217 ainda fora de database.generated.ts: cliente sem tipo, como
// em previsao-servopa/dados.ts. Os tipos de retorno ficam em feedLogica.
const sb = supabase as unknown as {
  from: (table: string) => any;
  rpc: (fn: string, args?: Record<string, unknown>) => any;
};
const CAMPOS_POST = 'id, autor_id, autor_nome, texto, anexos, publico, equipe_gerente, fixado_em, created_at, editado_em';

/** Avisa sino, cartão do Início geral e página que algo mudou no feed. */
export const EVENTO_FEED = 'north:feed-mudou';
export const avisarFeedMudou = () => window.dispatchEvent(new Event(EVENTO_FEED));

const falha = (error: { message?: string } | null, contexto: string) => {
  if (error) throw new Error(`${contexto}: ${error.message ?? 'erro desconhecido'}`);
};

export async function listarPosts(limite = 50): Promise<Post[]> {
  const { data, error } = await sb
    .from('feed_posts')
    .select(CAMPOS_POST)
    .order('created_at', { ascending: false })
    .limit(limite);
  falha(error, 'Não foi possível carregar o feed');
  return ((data ?? []) as Record<string, unknown>[]).map(normalizarPost);
}

export async function listarInteracoes(postIds: string[]): Promise<{
  curtidas: Curtida[];
  comentarios: Comentario[];
  visualizacoes: Map<string, number>;
}> {
  if (!postIds.length) return { curtidas: [], comentarios: [], visualizacoes: new Map() };
  const [c, m, v] = await Promise.all([
    sb
    .from('feed_curtidas').select('post_id, user_id, user_nome').in('post_id', postIds),
    sb
    .from('feed_comentarios')
      .select('id, post_id, autor_id, autor_nome, texto, created_at')
      .in('post_id', postIds)
      .order('created_at', { ascending: true }),
    sb.rpc('feed_contagem_visualizacoes', { p_posts: postIds }),
  ]);
  return {
    curtidas: ((c.data ?? []) as Record<string, unknown>[]).map(normalizarCurtida),
    comentarios: ((m.data ?? []) as Record<string, unknown>[]).map(normalizarComentario),
    visualizacoes: new Map(
      ((v.data ?? []) as { post_id: string; total: number }[]).map((r) => [r.post_id, Number(r.total)]),
    ),
  };
}

/** Os posts que EU já vi (a RLS devolve só as minhas visualizações… e as de todos para super admin). */
export async function meusVistos(euId: string, postIds: string[]): Promise<Set<string>> {
  if (!postIds.length) return new Set();
  const { data } = await sb
    .from('feed_visualizacoes')
    .select('post_id')
    .eq('user_id', euId)
    .in('post_id', postIds);
  return new Set(((data ?? []) as { post_id: string }[]).map((r) => r.post_id));
}

/** Quem viu (só super admin enxerga). */
export async function quemViu(postId: string): Promise<{ user_nome: string; visto_em: string }[]> {
  const { data } = await sb
    .from('feed_visualizacoes')
    .select('user_nome, visto_em')
    .eq('post_id', postId)
    .order('visto_em', { ascending: false });
  return (data ?? []) as { user_nome: string; visto_em: string }[];
}

export async function marcarVistos(postIds: string[]): Promise<void> {
  if (!postIds.length) return;
  // Já visto = conflito na chave (post, user): ignora, não é erro.
  await sb
    .from('feed_visualizacoes')
    .upsert(postIds.map((post_id) => ({ post_id })), { onConflict: 'post_id,user_id', ignoreDuplicates: true });
  avisarFeedMudou();
}

export async function curtir(postId: string, curtido: boolean): Promise<void> {
  if (curtido) {
    const { error } = await sb
    .from('feed_curtidas').delete().eq('post_id', postId).eq('user_id', (await euId()) ?? '');
    falha(error, 'Não foi possível descurtir');
  } else {
    const { error } = await sb
    .from('feed_curtidas').insert({ post_id: postId });
    falha(error, 'Não foi possível curtir');
  }
}

export async function comentar(postId: string, texto: string): Promise<void> {
  const { error } = await sb
    .from('feed_comentarios').insert({ post_id: postId, texto: texto.trim() });
  falha(error, 'Não foi possível comentar');
}

export async function apagarComentario(id: string): Promise<void> {
  const { error } = await sb
    .from('feed_comentarios').delete().eq('id', id);
  falha(error, 'Não foi possível apagar o comentário');
}

async function euId(): Promise<string | null> {
  const { data } = await supabase.auth.getSession();
  return data.session?.user.id ?? null;
}

/**
 * Publica: primeiro os arquivos (em <id>/…), depois o post com a lista deles.
 * O id nasce aqui para o caminho dos arquivos já apontar para o post.
 */
export async function publicar(dados: {
  texto: string;
  arquivos: File[];
  publico: 'todos' | 'equipe';
  equipeGerente: number | null;
  fixar: boolean;
}): Promise<void> {
  const id = crypto.randomUUID();
  const anexos: Anexo[] = [];
  for (const [i, f] of dados.arquivos.entries()) {
    const caminho = `${id}/${i + 1}-${nomeNoBucket(f.name)}`;
    const { error } = await supabase.storage.from(BUCKET).upload(caminho, f, { contentType: f.type || undefined });
    falha(error, `Não foi possível enviar ${f.name}`);
    anexos.push({ caminho, nome: f.name, tipo: f.type || 'application/octet-stream', tamanho: f.size });
  }
  const { error } = await sb
    .from('feed_posts').insert({
    id,
    texto: dados.texto.trim(),
    anexos,
    publico: dados.publico,
    equipe_gerente: dados.publico === 'equipe' ? dados.equipeGerente : null,
    fixado_em: dados.fixar ? new Date().toISOString() : null,
  });
  if (error) {
    // Post não nasceu: não deixa arquivo órfão no bucket.
    if (anexos.length) await supabase.storage.from(BUCKET).remove(anexos.map((a) => a.caminho));
    falha(error, 'Não foi possível publicar');
  }
  avisarFeedMudou();
}

export async function fixar(postId: string, fixado: boolean): Promise<void> {
  const { error } = await sb
    .from('feed_posts')
    .update({ fixado_em: fixado ? new Date().toISOString() : null })
    .eq('id', postId);
  falha(error, 'Não foi possível fixar');
  avisarFeedMudou();
}

export async function apagarPost(post: Pick<Post, 'id' | 'anexos'>): Promise<void> {
  const { error } = await sb
    .from('feed_posts').delete().eq('id', post.id);
  falha(error, 'Não foi possível apagar o post');
  if (post.anexos.length) await supabase.storage.from(BUCKET).remove(post.anexos.map((a) => a.caminho));
  avisarFeedMudou();
}

/** URLs temporárias (1 h) dos arquivos — o bucket é privado. */
export async function urlsDosArquivos(caminhos: string[]): Promise<Map<string, string>> {
  if (!caminhos.length) return new Map();
  try {
    const { data } = await supabase.storage.from(BUCKET).createSignedUrls(caminhos, 3600);
    return new Map(
      ((data ?? []) as { path: string | null; signedUrl: string }[])
        .filter((d) => d.path && d.signedUrl)
        .map((d) => [d.path!, d.signedUrl]),
    );
  } catch {
    return new Map();
  }
}

/**
 * Equipes que não aparecem no seletor de público do Feed (pedido do Pedro,
 * 28/10/2026): a equipe continua no cadastro e os posts antigos dela também.
 * 75 = PEDRO EILERS (mesmo id no DEV e no PROD).
 */
export const EQUIPES_OCULTAS_NO_FEED = new Set([75]);

/** Gerentes (quem tem alguém embaixo) — para "somente a equipe de…". */
export async function listarGerentes(): Promise<{ id: number; name: string }[]> {
  const { data } = await supabase.from('users').select('id, name, manager_id, disabled');
  const linhas = (data ?? []) as { id: number; name: string; manager_id: number | null; disabled: boolean | null }[];
  const comEquipe = new Set(linhas.filter((u) => u.manager_id != null && !u.disabled).map((u) => u.manager_id!));
  return linhas
    .filter((u) => comEquipe.has(u.id) && !u.disabled)
    .map((u) => ({ id: u.id, name: u.name }))
    .sort((a, b) => a.name.localeCompare(b.name, 'pt-BR'));
}
